import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Clock, 
  BookOpen, 
  Video, 
  FileText, 
  HelpCircle,
  Home,
  ChevronRight,
  Play,
  Lock,
  Star,
  Award,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { modules } from '../data/mockData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const contentIconMap = {
  reading: FileText,
  video: Video,
};

const CoursePage = ({ category, onBack, onComplete }) => {
  const [currentModule, setCurrentModule] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [viewingContent, setViewingContent] = useState(false);
  const [lessonProgress, setLessonProgress] = useState({});
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);

  const categoryNames = {
    religiosa: 'Religiosa',
    educadora: 'Educadora',
    familia: 'Familia Acogiente',
  };

  const module = modules[currentModule];
  const totalProgress = (completedModules.length / modules.length) * 100;
  const isModuleCompleted = completedModules.includes(currentModule);
  const allModulesCompleted = completedModules.length === modules.length;

  const completeCurrentModule = () => {
    if (!completedModules.includes(currentModule)) {
      setCompletedModules([...completedModules, currentModule]);
    }
    if (currentModule < modules.length - 1) {
      setCurrentModule(currentModule + 1);
      setCurrentLesson(0);
      setViewingContent(false);
    }
  };

  const handleLessonComplete = (lessonIndex) => {
    const key = `${currentModule}-${lessonIndex}`;
    setLessonProgress({ ...lessonProgress, [key]: true });
  };

  const handleOpenLesson = (item, index) => {
    if (item.type === 'video' && item.videoUrl) {
      window.open(item.videoUrl, '_blank');
      handleLessonComplete(index);
    } else {
      setSelectedLesson({ ...item, index });
      setIsLessonModalOpen(true);
    }
  };

  const handleCloseLesson = () => {
    setIsLessonModalOpen(false);
    setSelectedLesson(null);
  };

  const handleMarkAsComplete = () => {
    if (selectedLesson) {
      handleLessonComplete(selectedLesson.index);
      handleCloseLesson();
    }
  };

  const getModuleLessonProgress = (moduleIndex) => {
    const moduleContent = modules[moduleIndex].content;
    const completedLessons = moduleContent.filter((_, i) => 
      lessonProgress[`${moduleIndex}-${i}`]
    ).length;
    return (completedLessons / moduleContent.length) * 100;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Volver</span>
              </button>
              <div className="h-6 w-px bg-primary/30" />
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground text-sm">
                  {categoryNames[category]}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Progreso:</span>
                <div className="w-32 h-2 bg-primary/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                    style={{ width: `${totalProgress}%` }}
                  />
                </div>
                <span className="text-foreground text-sm font-medium">
                  {Math.round(totalProgress)}%
                </span>
              </div>
              {allModulesCompleted && (
                <Button
                  onClick={onComplete}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Finalizar
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
            <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[300px_1fr] gap-8">
            {/* Sidebar - Module List */}
            <div className="lg:sticky lg:top-24 lg:h-[calc(100vh-120px)] overflow-y-auto">
              <div className="space-y-3">
                {modules.map((mod, index) => {
                  const isCompleted = completedModules.includes(index);
                  const isCurrent = currentModule === index;
                  const isLocked =
                    index > 0 &&
                    !completedModules.includes(index - 1) &&
                    !completedModules.includes(index);

                  return (
                    <button
                      key={mod.id}
                      onClick={() => !isLocked && setCurrentModule(index)}
                      disabled={isLocked}
                      className={`w-full p-4 rounded-xl border transition-all duration-300 text-left ${
                        isCurrent
                          ? 'bg-primary/20 border-primary'
                          : isCompleted
                          ? 'bg-primary/10 border-primary/30 hover:bg-primary/20'
                          : isLocked
                          ? 'bg-primary-foreground/40 border-primary/10 opacity-50 cursor-not-allowed'
                          : 'bg-primary-foreground/60 border-primary/20 hover:bg-primary-foreground/80'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCompleted
                              ? 'bg-primary'
                              : isCurrent
                              ? 'bg-primary'
                              : isLocked
                              ? 'bg-primary/20'
                              : 'bg-primary/30'
                          }`}
                        >
                          {isCompleted ? (
                            <Check className="w-4 h-4 text-primary-foreground" />
                          ) : isLocked ? (
                            <Lock className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <span className="text-primary-foreground text-sm font-bold">
                              {mod.id}
                            </span>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-medium text-sm truncate ${
                              isCurrent
                                ? 'text-foreground'
                                : isCompleted
                                ? 'text-primary'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {mod.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{mod.duration}</span>
                          </div>
                        </div>
                      </div>

                      {isCurrent && (
                        <div className="mt-3">
                          <Progress
                            value={getModuleLessonProgress(index)}
                            className="h-1"
                          />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Content */}
            <div>
              {/* Module Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 text-primary text-sm mb-2">
                  <span>
                    Módulo {module.id} de {modules.length}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-muted-foreground">
                    {module.duration}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {module.title}
                </h1>
                <p className="text-muted-foreground text-lg">
                  {module.description}
                </p>
              </div>

              {/* Module Content */}
              <div className="space-y-4">
                {module.content.map((item, index) => {
                  const Icon = contentIconMap[item.type] || FileText;
                  const isLessonComplete =
                    lessonProgress[`${currentModule}-${index}`];
                  const isVideo = item.type === 'video';

                  return (
                    <div
                      key={index}
                      onClick={() => handleOpenLesson(item, index)}
                      className={`p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${
                        isLessonComplete
                          ? 'bg-primary/10 border-primary/30'
                          : 'bg-primary-foreground/60 border-primary/20 hover:bg-primary-foreground/80 hover:border-primary/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`p-3 rounded-xl ${
                              isLessonComplete
                                ? 'bg-primary/20 border border-primary/30'
                                : isVideo
                                ? 'bg-accent/20 border border-accent/30'
                                : 'bg-primary/20 border border-primary/30'
                            }`}
                          >
                            <Icon
                              className={`w-5 h-5 ${
                                isLessonComplete
                                  ? 'text-primary'
                                  : isVideo
                                  ? 'text-accent'
                                  : 'text-primary'
                              }`}
                            />
                          </div>
                          <div>
                            <h3 className="text-foreground font-medium">
                              {item.title}
                            </h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span>{isVideo ? 'Video' : 'Lectura'}</span>
                              {item.duration && (
                                <>
                                  <span>•</span>
                                  <span>{item.duration}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            isLessonComplete
                              ? 'bg-primary/20 text-primary'
                              : isVideo
                              ? 'bg-accent/20 text-accent'
                              : 'bg-primary/20 text-muted-foreground'
                          }`}
                        >
                          {isLessonComplete ? (
                            <span className="flex items-center gap-1">
                              <Check className="w-4 h-4" /> Completado
                            </span>
                          ) : isVideo ? (
                            <span className="flex items-center gap-1">
                              <Play className="w-4 h-4" /> Ver Video
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" /> Leer
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Module Navigation */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-primary/20">
                <Button
                  onClick={() =>
                    setCurrentModule(Math.max(0, currentModule - 1))
                  }
                  disabled={currentModule === 0}
                  variant="outline"
                  className="border-primary/30 text-muted-foreground hover:bg-primary/10 disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Módulo Anterior
                </Button>

                {currentModule < modules.length - 1 ? (
                  <Button
                    onClick={completeCurrentModule}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Completar y Continuar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      completeCurrentModule();
                      onComplete();
                    }}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Finalizar Curso
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lesson Content Modal */}
      <Dialog open={isLessonModalOpen} onOpenChange={handleCloseLesson}>
        <DialogContent className="bg-accent/80 border border-primary/30 backdrop-blur-xl max-w-3xl max-h-[85vh] p-0 overflow-hidden [&>button]:hidden">
          <button
            onClick={handleCloseLesson}
            className="absolute top-4 right-4 text-primary-foreground/70 hover:text-primary-foreground transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-accent/10 pointer-events-none" />

          {selectedLesson && (
            <div className="relative p-8 overflow-y-auto max-h-[85vh]">
              <DialogHeader className="mb-6">
                <div className="flex items-center gap-2 text-white text-sm mb-2">
                  <FileText className="w-4 h-4" />
                  <span>
                    Lectura • {selectedLesson.duration}
                  </span>
                </div>
                <DialogTitle className="text-2xl font-bold text-primary-foreground">
                  {selectedLesson.title}
                </DialogTitle>
              </DialogHeader>

              <div className="prose max-w-none">
                <div className="text-primary-foreground/90 leading-relaxed whitespace-pre-line">
                  {selectedLesson.text}
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 mt-8 pt-6 border-t border-primary-foreground/20">
                <Button
                  onClick={handleCloseLesson}
                  variant="outline"
                  className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                >
                  Cerrar
                </Button>
                <Button
                  onClick={handleMarkAsComplete}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Marcar como Completado
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursePage;
